import { EmailService, EmailData, StatusReportData } from '@/types/email';

class OutlookEmailService implements EmailService {
  private async getOutlookAccessToken(): Promise<string> {
    // In a real implementation, this would handle OAuth 2.0 flow with Microsoft Graph API
    // For demo purposes, we'll use a mock implementation
    return new Promise((resolve, reject) => {
      // Simulate OAuth flow
      setTimeout(() => {
        resolve('mock_outlook_access_token');
      }, 1000);
    });
  }

  async sendEmail(emailData: EmailData): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      // For Outlook integration, we would use Microsoft Graph API
      // This is a simplified implementation that opens the user's default email client
      
      const recipients = emailData.to.map(r => `${r.name ? `"${r.name}" ` : ''}<${r.email}>`).join(';');
      const ccRecipients = emailData.cc?.map(r => `${r.name ? `"${r.name}" ` : ''}<${r.email}>`).join(';');
      const bccRecipients = emailData.bcc?.map(r => `${r.name ? `"${r.name}" ` : ''}<${r.email}>`).join(';');
      
      let mailtoUrl = `mailto:${recipients}`;
      
      const params = new URLSearchParams();
      params.set('subject', emailData.subject);
      params.set('body', emailData.body);
      
      if (ccRecipients) {
        params.set('cc', ccRecipients);
      }
      
      if (bccRecipients) {
        params.set('bcc', bccRecipients);
      }
      
      mailtoUrl += `?${params.toString()}`;
      
      // Open in default email client (which could be Outlook)
      window.open(mailtoUrl, '_blank');
      
      return {
        success: true,
        message: 'Email opened in default email client. Please send the email manually.'
      };
      
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send email'
      };
    }
  }

  async sendStatusReport(
    clientEmail: string, 
    clientName: string, 
    reportData: StatusReportData
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    const subject = `Tax Ready Status Report - ${clientName} - ${reportData.fiscalYear}${reportData.quarter ? ` - ${reportData.quarter}` : ''}`;
    
    const body = this.generateStatusReportEmailBody(reportData);
    
    const emailData: EmailData = {
      to: [{ email: clientEmail, name: clientName }],
      subject,
      body,
      isHtml: false
    };
    
    return this.sendEmail(emailData);
  }

  private generateStatusReportEmailBody(reportData: StatusReportData): string {
    const formatDate = (date: Date) => date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    return `
Dear ${reportData.clientName},

Please find below your Tax Ready status report for ${reportData.fiscalYear}${reportData.quarter ? ` - ${reportData.quarter}` : ''}.

===========================================
TAX READY STATUS REPORT
===========================================

Client: ${reportData.clientName}
Period: ${reportData.fiscalYear}${reportData.quarter ? ` - ${reportData.quarter}` : ''}
Report Generated: ${formatDate(reportData.generatedAt)}

TASK STATUS SUMMARY:
--------------------
BAS Status: ${reportData.basStatus}
Bookkeeping Status: ${reportData.bookkeepingStatus}
Payroll Status: ${reportData.payrollStatus}
Super Payment Status: ${reportData.superStatus}
Invoices Status: ${reportData.invoicesStatus}

REMARKS:
--------
${reportData.remarks || 'No additional remarks.'}

===========================================
This is an automated status report from Tax Ready.
If you have any questions or need assistance, 
please don't hesitate to contact our team.

Thank you for choosing Tax Ready!
===========================================
    `.trim();
  }
}

// Export singleton instance
export const emailService = new OutlookEmailService();